/** Contains helper functions for task tree manipulation and filtering within the TasksContext. */
// @ts-check
import {
  getTaskProjects,
  getTaskContexts,
} from '@/features/tasks/utils/data/taskTags';

/**
 * @param {string} taskId
 * @param {any[]} taskTree
 * @returns {string[]}
 */
export const getAllDescendantIds = (taskId, taskTree) => {
  if (!taskTree) {
    console.warn('getAllDescendantIds called before taskTree was initialized.');
    return [];
  }
  const descendants = [];
  const node = taskTree.find((n) => n.id === taskId);

  if (!node) {
    if (import.meta.env.MODE !== 'test') {
      console.warn(
        `[getAllDescendantIds] Taak ${taskId} niet gevonden in taskTree (${taskTree.length} nodes)`,
      );
    }
    return [];
  }

  if (!node.children || node.children.length === 0) {
    return [];
  }

  const findChildren = (children) => {
    for (const child of children) {
      descendants.push(child.id);
      if (child.children && child.children.length > 0) {
        findChildren(child.children);
      }
    }
  };

  findChildren(node.children);
  return descendants;
};

/**
 * @param {Task[]} allTasks
 * @param {string[]} hiddenProjects
 * @param {string[]} hiddenContexts
 * @returns {Task[]}
 */
export const applyFilters = (allTasks, hiddenProjects, hiddenContexts) => {
  if (!allTasks) return [];
  if (
    !hiddenProjects ||
    !hiddenContexts ||
    (hiddenProjects.length === 0 && hiddenContexts.length === 0)
  ) {
    return allTasks;
  }

  const hiddenProjectSet = new Set(
    hiddenProjects.map((p) => String(p).toLowerCase()),
  );
  const hiddenContextSet = new Set(
    hiddenContexts.map((c) => String(c).toLowerCase()),
  );

  return allTasks.filter((task) => {
    const taskProjects = getTaskProjects(task)
      .filter(Boolean)
      .map((p) => String(p).trim().toLowerCase());
    const taskContexts = getTaskContexts(task)
      .filter(Boolean)
      .map((c) => String(c).trim().toLowerCase());

    const hasHiddenProject =
      taskProjects.length > 0 &&
      taskProjects.some((p) => hiddenProjectSet.has(p));
    const hasHiddenContext =
      taskContexts.length > 0 &&
      taskContexts.some((c) => hiddenContextSet.has(c));

    if (hiddenProjects.length > 0 && hiddenContexts.length > 0) {
      return !hasHiddenProject && !hasHiddenContext;
    } else if (hiddenProjects.length > 0) {
      return !hasHiddenProject;
    } else if (hiddenContexts.length > 0) {
      return !hasHiddenContext;
    }

    return true;
  });
};

/**
 * @param {Task[]} allTasks
 * @param {Map<string, string>} masterMap
 * @param {(task: Task) => string[]} idExtractor
 * @returns {Map<string, string>}
 */
export const buildActiveMap = (allTasks, masterMap, idExtractor) => {
  if (!allTasks) return new Map();
  const uncompletedTasks = allTasks.filter((task) => !task.completed);
  const activeIds = new Set();
  uncompletedTasks.forEach((task) => {
    idExtractor(task)
      .filter(Boolean)
      .forEach((id) => activeIds.add(String(id).trim().toLowerCase()));
  });
  const newMap = new Map();
  for (const [id, name] of masterMap.entries()) {
    if (activeIds.has(id)) newMap.set(id, name);
  }
  return newMap;
};

/**
 * @param {import('firebase/firestore').QuerySnapshot} snapshot
 * @returns {Map<string, string>}
 */
export const processSnapshotToMap = (snapshot) => {
  const newMap = new Map();
  snapshot.forEach((doc) => {
    const data = doc.data();
    if (data && data.name) {
      const docId = doc.id.toLowerCase().trim();
      const docName = data.name.trim();
      if (docId && docName) {
        newMap.set(docId, docName);
      }
    }
  });
  return newMap;
};

const PROJECT_COLOR_MAX_INDEX = 7;

/**
 * @param {import('firebase/firestore').QuerySnapshot} snapshot
 * @returns {{
 *   displayMap: Map<string, string>,
 *   colorMap: Map<string, number>,
 *   missingColorIds: string[],
 * }}
 */
export const processProjectSnapshot = (snapshot) => {
  const displayMap = new Map();
  const colorMap = new Map();
  const missingColorIds = [];

  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (!data?.name) return;
    const docId = docSnap.id.toLowerCase().trim();
    const docName = data.name.trim();
    if (!docId || !docName) return;

    displayMap.set(docId, docName);
    const color = data.color;
    if (
      Number.isInteger(color) &&
      color >= 0 &&
      color <= PROJECT_COLOR_MAX_INDEX
    ) {
      colorMap.set(docId, color);
    } else {
      missingColorIds.push(docId);
    }
  });

  return { displayMap, colorMap, missingColorIds };
};

/**
 * Applies the "show only" (active) project/context filters on top of the hidden-based filter.
 * OR within the same category (#project x #project), AND between #project and @context.
 * Empty filters are no-ops, so default behavior is identical to the unfiltered input.
 *
 * @param {Task[]|null|undefined} allTasks
 * @param {string[]} projectFilter - project ids to show (case-insensitive)
 * @param {string[]} contextFilter - context ids to show (case-insensitive)
 * @returns {Task[]}
 */
/**
 * Default project/context for new tasks when exactly one filter chip is active.
 * Typed #project / @context in taskData take precedence; multiple active filters
 * in a category are ignored (no guess which one to pick).
 *
 * @param {string[]} projectFilter
 * @param {string[]} contextFilter
 * @param {Partial<NewTask>} [taskData]
 * @returns {Partial<NewTask>}
 */
export const getActiveFilterTaskDefaults = (
  projectFilter,
  contextFilter,
  taskData = {},
) => {
  const defaults = {};
  const hasProjects = (taskData.projects?.length ?? 0) > 0;
  const hasContexts = (taskData.contexts?.length ?? 0) > 0;

  if (!hasProjects && projectFilter?.length === 1) {
    defaults.projects = [projectFilter[0]];
  }
  if (!hasContexts && contextFilter?.length === 1) {
    defaults.contexts = [contextFilter[0]];
  }

  return defaults;
};

/**
 * Merge parsed quick-add input with optional page defaults and active filter chips.
 * Non-empty parsed #project / @context win; empty parser arrays do not erase defaults.
 *
 * @param {Partial<NewTask>} defaultTaskData
 * @param {Partial<NewTask>} parsedData
 * @param {string[]} projectFilter
 * @param {string[]} contextFilter
 * @returns {Partial<NewTask>}
 */
export const mergeNewTaskCreateData = (
  defaultTaskData,
  parsedData,
  projectFilter,
  contextFilter,
) => {
  defaultTaskData = defaultTaskData ?? {};
  const filterDefaults = getActiveFilterTaskDefaults(
    projectFilter,
    contextFilter,
    { ...defaultTaskData, ...parsedData },
  );
  const merged = { ...defaultTaskData, ...parsedData };

  if (!parsedData.projects?.length && filterDefaults.projects) {
    merged.projects = filterDefaults.projects;
  }
  if (!parsedData.contexts?.length && filterDefaults.contexts) {
    merged.contexts = filterDefaults.contexts;
  }
  // parseTaskInput always sets date: null when no >token — keep page defaults.
  if (
    (parsedData.date === null || parsedData.date === undefined) &&
    defaultTaskData.date
  ) {
    merged.date = defaultTaskData.date;
  }

  return merged;
};

export const applyActiveFilters = (allTasks, projectFilter, contextFilter) => {
  if (!allTasks) return [];
  const projectIds = (projectFilter || [])
    .map((p) => String(p).trim().toLowerCase())
    .filter(Boolean);
  const contextIds = (contextFilter || [])
    .map((c) => String(c).trim().toLowerCase())
    .filter(Boolean);
  if (projectIds.length === 0 && contextIds.length === 0) return allTasks;

  const projectSet = new Set(projectIds);
  const contextSet = new Set(contextIds);

  return allTasks.filter((task) => {
    const taskProjects = getTaskProjects(task)
      .filter(Boolean)
      .map((p) => String(p).trim().toLowerCase());
    const taskContexts = getTaskContexts(task)
      .filter(Boolean)
      .map((c) => String(c).trim().toLowerCase());

    const matchesProject =
      projectIds.length === 0 || taskProjects.some((p) => projectSet.has(p));
    const matchesContext =
      contextIds.length === 0 || taskContexts.some((c) => contextSet.has(c));

    return matchesProject && matchesContext;
  });
};
